import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ErrorBox } from "@/components/common";
import { AuthShell } from "@/components/AuthShell";
import { useApp } from "@/lib/store";
import { parseApiError } from "@/lib/api/client";
import { LogIn, KeyRound, ShieldCheck } from "lucide-react";

interface LoginResponse {
  token: string;
  user?: { id: number; username: string; email: string;
           is_staff?: boolean; is_superuser?: boolean };
  workspaces?: Array<{ id: string; name: string; slug: string;
                       organization?: string; organization_slug?: string;
                       organization_name?: string }>;
  default_workspace_id?: string | null;
  default_tenant_schema?: string | null;
}

const API_BASE = (import.meta.env.VITE_API_BASE_URL || "/api/v1").replace(/\/+$/, "");
const API_ROOT = API_BASE.endsWith("/api/v1")
  ? API_BASE.slice(0, -"/api/v1".length)
  : "";

export function Login() {
  const nav = useNavigate();
  const setAuth = useApp((s) => s.setAuth);
  const setWorkspaces = useApp((s) => s.setWorkspaces);
  const setWorkspace = useApp((s) => s.setWorkspace);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(localStorage.getItem("ckos.token") ?? "");
  const [tenant, setTenant] = useState(localStorage.getItem("ckos.tenantSchema") ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function acceptLogin(data: LoginResponse) {
    setAuth(data.token, data.user ?? { id: 0, username: "federated", email: "" });
    if (data.workspaces?.length) {
      setWorkspaces(data.workspaces);
      setWorkspace(data.default_workspace_id ?? data.workspaces[0].id);
    } else if (data.default_tenant_schema) {
      localStorage.setItem("ckos.tenantSchema", data.default_tenant_schema);
    }
    nav({ to: "/dashboard" });
  }

  useEffect(() => {
    const state = new URLSearchParams(window.location.search).get("oidc");
    if (state === "failed") setError("Enterprise sign-in failed. Check the provider configuration and try again.");
    if (state !== "complete") return;
    setBusy(true);
    fetch(`${API_BASE}/iam/oidc/exchange/`, { credentials: "include" })
      .then(async (response) => {
        if (!response.ok) throw await parseApiError(response);
        return response.json() as Promise<LoginResponse>;
      })
      .then(acceptLogin)
      .catch((reason) => setError(reason instanceof Error ? reason.message : String(reason)))
      .finally(() => setBusy(false));
  }, []);

  function startSso() {
    const schema = tenant.trim().toLowerCase();
    if (!schema) {
      setError("Enter your organization slug to continue with SSO.");
      return;
    }
    localStorage.setItem("ckos.tenantSchema", schema);
    window.location.assign(`${API_BASE}/iam/oidc/authenticate/?tenant=${encodeURIComponent(schema)}`);
  }

  async function doLogin(e?: React.FormEvent) {
    e?.preventDefault();
    setBusy(true); setError(null);
    try {
      // Preferred: rich /api/v1/iam/login/ — returns token + user envelope.
      let r = await fetch(`${API_BASE}/iam/login/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      // Fallback to vanilla DRF authtoken if iam isn't mounted yet.
      if (r.status === 404) {
        r = await fetch(`${API_ROOT}/api-token-auth/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username, password }),
        });
      }
      if (!r.ok) {
        throw await parseApiError(r);
      }
      const data = (await r.json()) as LoginResponse;
      // Pin the user to their tenant so cross-tenant data leaks are
      // structurally impossible: store the workspaces list, then select
      // the default one (which also stamps X-Tenant-Schema for the API
      // client). A user belonging to "acme" will land on acme — never
      // on the public/marketing schema.
      acceptLogin(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  function saveManual() {
    if (!token.trim()) return;
    setAuth(token.trim(), { id: 0, username: "manual", email: "" });
    nav({ to: "/dashboard" });
  }

  return (
    <AuthShell
      eyebrow="Welcome back"
      title="Sign in to your workspace"
      subtitle="Pick up where you left off — your tenant, your data, your governance."
      bullets={[
        "Land directly on your tenant — never the public schema",
        "Every action cited, permissioned and replayable",
        "Budget caps and audit on every run",
      ]}
    >
      <div className="mb-6">
        <h1 className="text-xl font-semibold tracking-tight">Sign in</h1>
        <p className="mt-1 text-sm text-muted">Welcome back to your Ontoligent workspace.</p>
      </div>

      <form onSubmit={doLogin}>
        <div className="mb-3 flex items-center gap-2 text-sm font-medium">
          <LogIn size={16} className="text-accent" /> Account login
        </div>
        <label className="label" htmlFor="li-user">Username or email</label>
        <input id="li-user" className="input mb-3" autoFocus
               value={username} onChange={(e) => setUsername(e.target.value)} />
        <label className="label" htmlFor="li-pwd">Password</label>
        <input id="li-pwd" className="input mb-3" type="password"
               value={password} onChange={(e) => setPassword(e.target.value)} />
        <button className="btn-primary w-full justify-center py-2.5" disabled={busy} type="submit">
          {busy ? "Signing in…" : "Sign in"}
        </button>
        {error && <div className="mt-3"><ErrorBox error={error} /></div>}

        <div className="mt-4 text-center text-xs text-muted">
          New here?{" "}
          <Link to="/signup" className="text-accent underline">
            Create your tenant →
          </Link>
        </div>
        <div className="mt-2 text-center text-xs">
          <Link to="/reset-password" className="text-muted underline hover:text-accent">
            Forgot your password?
          </Link>
        </div>
      </form>

      <div className="my-5 flex items-center gap-3 text-xs text-muted">
        <span className="h-px flex-1 bg-border" />or<span className="h-px flex-1 bg-border" />
      </div>
      <div>
        <div className="mb-3 flex items-center gap-2 text-sm font-medium">
          <ShieldCheck size={16} className="text-accent" /> Enterprise SSO
        </div>
        <label className="label" htmlFor="li-tenant">Organization slug</label>
        <input id="li-tenant" className="input mb-3" placeholder="acme"
               value={tenant} onChange={(event) => setTenant(event.target.value)} />
        <button className="btn w-full justify-center" type="button" disabled={busy} onClick={startSso}>
          <ShieldCheck size={16} /> Continue with SSO
        </button>
      </div>

      <details className="mt-6 border-t border-border pt-4">
        <summary className="flex cursor-pointer items-center gap-2 text-xs font-medium text-muted hover:text-text">
          <KeyRound size={14} className="text-accent" /> Paste a token (service accounts & CI)
        </summary>
        <div className="mt-3">
          <p className="mb-3 text-xs text-muted">
            For service accounts and CI. Paste a DRF token to bypass the login form.
          </p>
          <textarea className="input mb-3 h-24 font-mono text-xs"
                    placeholder="paste DRF token…"
                    value={token} onChange={(e) => setToken(e.target.value)} />
          <button className="btn w-full" onClick={saveManual}>Save token</button>
        </div>
      </details>
    </AuthShell>
  );
}
